import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { DepositApi } from '../api/DepositApi';
import { ProductApi } from '../api/ProductApi';
import { ProductTransfer, ProductTransferApi } from '../api/ProductTransferApi';
import { Deposit, DepositProduct } from '../types/Product';
import { useTranslation } from 'react-i18next';
import './ProductTransferPage.css';

function errorMessage(error: any, fallback: string): string {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

export default function ProductTransferPage() {
  const { t, i18n } = useTranslation();
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [stock, setStock] = useState<DepositProduct[]>([]);
  const [transfers, setTransfers] = useState<ProductTransfer[]>([]);
  const [sourceId, setSourceId] = useState('');
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [dispatchCode, setDispatchCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [changePulse, setChangePulse] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const dispatchInput = useRef<HTMLInputElement>(null);
  const transferSignature = useRef('');

  const applyTransfers = useCallback((next: ProductTransfer[]) => {
    const signature = next.map(item => `${item.id}:${item.quantity}:${item.status}`).join('|');
    if (transferSignature.current && transferSignature.current !== signature) {
      setChangePulse(true);
      window.setTimeout(() => setChangePulse(false), 900);
    }
    transferSignature.current = signature;
    setTransfers(next);
    setLastUpdated(new Date());
  }, []);

  const reloadTransfers = useCallback(async (showError = true) => {
    try {
      const response = await ProductTransferApi.inTransit();
      applyTransfers(asArray<ProductTransfer>(response.data));
    } catch (e) {
      if (showError) setError(errorMessage(e, t('transfers.operationFailed')));
    }
  }, [applyTransfers, t]);

  useEffect(() => {
    const loadInitialData = async () => {
      setError('');
      const [depositResult, transferResult] = await Promise.allSettled([
        DepositApi.getDeposits(), ProductTransferApi.inTransit()
      ]);
      if (depositResult.status === 'fulfilled') {
        const list = asArray<Deposit>(depositResult.value.data);
        setDeposits(list);
        setSourceId(list[0]?.id?.toString() || '');
      } else {
        setDeposits([]);
        setError(errorMessage(depositResult.reason, t('transfers.depositsLoadFailed')));
      }
      if (transferResult.status === 'fulfilled') {
        const list = asArray<ProductTransfer>(transferResult.value.data);
        applyTransfers(list);
      } else {
        applyTransfers([]);
        setError(current => current || errorMessage(transferResult.reason, t('transfers.operationFailed')));
      }
    };
    void loadInitialData();
  }, [applyTransfers, t]);

  // Keep the board synchronized with scans made in another browser/device.
  useEffect(() => {
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') void reloadTransfers(false);
    };
    const interval = window.setInterval(refreshVisible, 4000);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [reloadTransfers]);

  useEffect(() => {
    if (!sourceId) {
      setStock([]);
      return;
    }
    DepositApi.getDepositProducts(Number(sourceId))
      .then(response => {
        const items = asArray<DepositProduct>(response.data);
        setStock(items.filter(item => item.quantity > 0));
        setProductId(current => items.some(item => item.product.id === Number(current)) ? current : '');
      })
      .catch(e => setError(errorMessage(e, t('transfers.operationFailed'))));
  }, [sourceId, t]);

  const dispatch = async (resolvedProductId?: number) => {
    const id = resolvedProductId ?? Number(productId);
    if (!sourceId || !id || quantity < 1) {
      setError(t('transfers.chooseDispatchFields'));
      return;
    }
    setBusy(true); setError(''); setMessage('');
    try {
      const response = await ProductTransferApi.dispatch(Number(sourceId), id, quantity);
      setMessage(t('transfers.dispatchedSuccess', { quantity: response.data.quantity, product: response.data.productName }));
      setDispatchCode('');
      applyTransfers([response.data, ...transfers.filter(item => item.id !== response.data.id)]);
      const stockResponse = await DepositApi.getDepositProducts(Number(sourceId));
      setStock(asArray<DepositProduct>(stockResponse.data).filter(item => item.quantity > 0));
      await reloadTransfers();
      dispatchInput.current?.focus();
    } catch (e) {
      setError(errorMessage(e, t('transfers.operationFailed')));
    } finally { setBusy(false); }
  };

  const scanDispatch = async (event: FormEvent) => {
    event.preventDefault();
    if (!dispatchCode.trim()) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const product = (await ProductApi.scan(dispatchCode)).data;
      if (!product.id || !stock.some(item => item.product.id === product.id)) {
        throw new Error(t('transfers.productNotInSource'));
      }
      setProductId(String(product.id));
      setBusy(false);
      await dispatch(product.id);
    } catch (e) {
      setError(errorMessage(e, t('transfers.operationFailed')));
      setBusy(false);
    }
  };

  const cancel = async (transfer: ProductTransfer) => {
    if (!window.confirm(t('transfers.confirmReturnSource', { quantity: transfer.quantity, product: transfer.productName, deposit: transfer.sourceDepositName }))) return;
    setBusy(true); setError(''); setMessage('');
    try {
      await ProductTransferApi.cancel(transfer.id);
      setMessage(t('transfers.cancelledSuccess'));
      applyTransfers(transfers.filter(item => item.id !== transfer.id));
      if (String(transfer.sourceDepositId) === sourceId) {
        const stockResponse = await DepositApi.getDepositProducts(Number(sourceId));
        setStock(asArray<DepositProduct>(stockResponse.data).filter(item => item.quantity > 0));
      }
      await reloadTransfers();
    } catch (e) { setError(errorMessage(e, t('transfers.operationFailed'))); }
    finally { setBusy(false); }
  };

  return (
    <div className="transfer-page">
      <div className="transfer-heading">
        <div><h2>{t('transfers.title')}</h2><p className="transfer-help">{t('transfers.scannerHelp')}</p></div>
        <div className="live-status"><span className="live-dot" />{t('transfers.live')} · {lastUpdated ? t('transfers.updatedAt', { time: lastUpdated.toLocaleTimeString(i18n.resolvedLanguage) }) : t('transfers.connecting')}</div>
      </div>
      {message && <div className="transfer-alert success">{message}</div>}
      {error && <div className="transfer-alert error">{error}</div>}

      <div className="transfer-grid">
        <section className="transfer-card">
          <h3>{t('transfers.dispatchTitle')}</h3>
          <label>{t('transfers.sourceDeposit')}<select value={sourceId} onChange={e => setSourceId(e.target.value)} disabled={busy}>
            <option value="">{t('transfers.chooseDeposit')}</option>{deposits.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select></label>
          <label>{t('transfers.productInStock')}<select value={productId} onChange={e => setProductId(e.target.value)} disabled={busy || !sourceId}>
            <option value="">{t('transfers.chooseOrScan')}</option>{stock.map(item => <option key={item.product.id} value={item.product.id}>{item.product.name} — {t('transfers.available', { quantity: item.quantity })}</option>)}
          </select></label>
          <label>{t('transfers.quantity')}<input type="number" min="1" value={quantity} onChange={e => setQuantity(Math.max(1, Number(e.target.value)))} disabled={busy} /></label>
          <form onSubmit={scanDispatch} className="scan-row">
            <input ref={dispatchInput} value={dispatchCode} onChange={e => setDispatchCode(e.target.value)} placeholder={t('transfers.scanDispatchPlaceholder')} disabled={busy || !sourceId} autoComplete="off" />
            <button type="submit" disabled={busy || !dispatchCode.trim()}>{t('transfers.scanAndDispatch')}</button>
          </form>
          <button className="secondary-action" onClick={() => dispatch()} disabled={busy || !productId}>{t('transfers.dispatchSelected')}</button>
          {deposits.length === 0 && <p className="manual-help">{t('transfers.noAccessibleDeposits')}</p>}
        </section>
      </div>

      <section className={`transfer-card transfer-list${changePulse ? ' changed' : ''}`}>
        <div className="list-title"><div><h3>{t('transfers.onRoadTitle')}</h3><span className="transit-summary">{t('transfers.transitSummary', { shipments: transfers.length, units: transfers.reduce((sum, item) => sum + item.quantity, 0) })}</span></div><button onClick={() => reloadTransfers()} disabled={busy}>{t('transfers.refreshNow')}</button></div>
        {transfers.length === 0 ? <p>{t('transfers.noneInTransit')}</p> : (
          <div className="transfer-table-wrap"><table><thead><tr><th></th><th>{t('transfers.product')}</th><th>{t('transfers.quantity')}</th><th>{t('transfers.from')}</th><th>{t('transfers.dispatched')}</th><th>{t('transfers.by')}</th><th></th></tr></thead>
            <tbody>{transfers.map(item => <tr key={item.id}>
              <td></td><td>{item.productName}</td><td>{item.quantity}</td><td>{item.sourceDepositName}</td><td>{new Date(item.dispatchedAt).toLocaleString(i18n.resolvedLanguage)}</td><td>{item.dispatchedBy}</td><td><button className="cancel-action" onClick={() => cancel(item)} disabled={busy}>{t('transfers.returnToSource')}</button></td>
            </tr>)}</tbody></table></div>
        )}
      </section>
    </div>
  );
}
